import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { GhostButton, Marquee, MaskedLines, PrimaryButton, Reveal, SectionTag } from "@/components/ui-bits";
import { CAPABILITY_CARDS, ENGAGEMENT_MODELS, INDUSTRY_SPOTLIGHT, IMAGES, SOLUTIONS, TAGLINE } from "@/content/site";
import api from "@/lib/api";

const MARQUEE_ITEMS = [
  "Cloud Engineering", "DevOps", "Data & AI", "Kubernetes", "Terraform", "Generative AI",
  "Software Engineering", "Quality Engineering", "AWS", "Azure", "Google Cloud", "Managed Services",
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-center overflow-hidden" data-testid="home-hero">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={IMAGES.hero} alt="" className="h-[115%] w-full object-cover opacity-25" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-tark-bg/60 via-tark-bg/80 to-tark-bg" />
      <div className="absolute inset-0 hero-grid opacity-50 [mask-image:radial-gradient(ellipse_75%_65%_at_50%_35%,black,transparent)]" />
      <div className="absolute inset-0 spotlight" />

      <div className="relative mx-auto w-full max-w-7xl px-6 pb-24 pt-44 lg:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-mono text-[11px] uppercase tracking-[0.4em] text-tark-cyan"
          data-testid="hero-tagline"
        >
          {TAGLINE}
        </motion.p>

        <h1 className="mt-8 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
          <MaskedLines
            lines={["Engineering Capabilities", "for the Modern Enterprise."]}
            lineClassName="last:text-gradient-cyan"
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-tark-mut sm:text-lg"
        >
          TARK is a global technology engineering company. We design, build and operate
          cloud platforms, DevOps pipelines, data &amp; AI systems and enterprise software
          for premium businesses across every industry that runs on technology.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <PrimaryButton to="/start-a-project" testId="hero-talk-to-expert">Talk to an Expert</PrimaryButton>
          <GhostButton to="/services" testId="hero-explore-services">Explore Our Services</GhostButton>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mt-20 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-tark-line/70 pt-6"
          data-testid="hero-pillars-strip"
        >
          {["Cloud", "DevOps", "Data & AI", "Software Engineering"].map((p) => (
            <span key={p} className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-tark-dim">
              <span className="h-1 w-1 rotate-45 bg-tark-cyan" /> {p}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const Capabilities = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="capabilities-section">
    <Reveal>
      <SectionTag testId="capabilities-eyebrow">What TARK does</SectionTag>
      <h2 className="mt-4 max-w-3xl font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
        Seven practices. One engineering standard.
      </h2>
    </Reveal>

    <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-6">
      {CAPABILITY_CARDS.map((card, i) => (
        <Reveal key={card.name} delay={(i % 3) * 0.08} className={i === 2 ? "lg:col-span-4 md:col-span-2" : "lg:col-span-2"}>
          <Link
            to={card.href}
            data-testid={`capability-card-${card.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`card-glow group flex h-full flex-col rounded-2xl border p-7 ${
              card.highlight
                ? "border-tark-blue/40 bg-gradient-to-br from-tark-card to-[#0A1A33]"
                : "border-tark-line bg-tark-card"
            }`}
          >
            <div className="flex items-start justify-between">
              <h3 className={`font-display text-lg font-bold tracking-tight ${card.highlight ? "text-tark-cyan" : "text-tark-ink"}`}>
                {card.name}
              </h3>
              <ArrowUpRight className="h-5 w-5 text-tark-dim transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-tark-cyan" />
            </div>
            <p className="mt-3 text-sm leading-relaxed text-tark-mut">{card.desc}</p>
            <ul className="mt-5 flex flex-wrap gap-2">
              {card.caps.map((c) => (
                <li key={c} className="rounded border border-tark-line/80 bg-tark-bg/60 px-2.5 py-1 font-mono text-[11px] text-tark-dim">
                  {c}
                </li>
              ))}
            </ul>
            <span className="mt-auto pt-6 font-display text-xs font-bold uppercase tracking-[0.2em] text-tark-blue transition-colors group-hover:text-tark-cyan">
              Learn More
            </span>
          </Link>
        </Reveal>
      ))}
      <Reveal delay={0.16} className="lg:col-span-2 md:col-span-2">
        <Link
          to="/start-a-project"
          data-testid="capability-card-cta"
          className="card-glow group flex h-full flex-col justify-between rounded-2xl border border-tark-blue bg-tark-blue p-7"
        >
          <h3 className="font-display text-lg font-bold tracking-tight text-white">Have a project in mind?</h3>
          <span className="mt-8 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.2em] text-white">
            Build With TARK <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </Link>
      </Reveal>
    </div>
  </section>
);

const SolutionsTeaser = () => (
  <section className="border-y border-tark-line bg-tark-surface/40 py-24 lg:py-32" data-testid="solutions-teaser">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <SectionTag>What TARK can build</SectionTag>
          <h2 className="mt-4 max-w-xl font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            Solutions engineered for real business outcomes.
          </h2>
        </div>
        <GhostButton to="/solutions" testId="solutions-teaser-link">All Solutions</GhostButton>
      </Reveal>
      <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-tark-line bg-tark-line sm:grid-cols-2 lg:grid-cols-5">
        {SOLUTIONS.slice(0, 10).map((s, i) => (
          <Reveal key={s.name} delay={(i % 5) * 0.05} className="bg-tark-bg">
            <Link to="/solutions" className="group flex h-full flex-col justify-between gap-6 p-6 transition-colors hover:bg-tark-card">
              <span className="font-mono text-[10px] text-tark-dim">{String(i + 1).padStart(2, "0")}</span>
              <span className="font-display text-sm font-bold leading-snug text-tark-ink group-hover:text-tark-cyan">{s.name}</span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const DataAISpotlight = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="data-ai-spotlight">
    <div className="grid items-center gap-12 lg:grid-cols-2">
      <Reveal>
        <SectionTag>Flagship Practice</SectionTag>
        <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
          Turn Data Into Intelligence.<br />
          <span className="text-gradient-cyan">Turn AI Into Business Impact.</span>
        </h2>
        <p className="mt-6 max-w-lg text-base leading-relaxed text-tark-mut">
          Modern data platforms, AI-powered applications and intelligent automation systems
          that transform information into measurable business value.
        </p>
        <div className="mt-9 flex flex-wrap gap-4">
          <PrimaryButton to="/start-a-project" testId="dataai-spot-cta">Talk to Our Data &amp; AI Experts</PrimaryButton>
          <GhostButton to="/data-ai" testId="dataai-spot-explore">Explore AI Capabilities</GhostButton>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="clip-frame relative overflow-hidden rounded-2xl border border-tark-line">
          <img src={IMAGES.dataAi} alt="Data and AI engineering" className="aspect-[4/3] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-tark-bg/80 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.3em] text-tark-cyan">
            Data Platforms · LLMs · AI Agents · Analytics
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const IndustrySpotlight = () => (
  <section className="relative overflow-hidden py-24 lg:py-32" data-testid="industry-spotlight">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_60%_at_80%_20%,rgba(0,229,255,0.07),transparent)]" />
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <Reveal>
          <SectionTag>Industries We Serve</SectionTag>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            Premium engineering for every industry that runs on technology.
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-tark-mut">
            From banking and healthcare to manufacturing, retail and logistics — TARK builds
            the software, cloud, data and AI systems that high-value businesses depend on,
            delivered to international enterprise standards.
          </p>
          <ul className="mt-8 grid gap-x-8 gap-y-3.5 sm:grid-cols-2" data-testid="industry-spotlight-list">
            {INDUSTRY_SPOTLIGHT.map((ind) => (
              <li key={ind} className="flex items-start gap-3 text-sm leading-snug text-tark-mut">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-tark-cyan" />
                {ind}
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap gap-4">
            <PrimaryButton to="/industries" testId="industry-spotlight-explore">Explore Industries</PrimaryButton>
            <GhostButton to="/start-a-project" testId="industry-spotlight-cta">Talk to an Expert</GhostButton>
          </div>
          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-tark-dim">
            Global delivery · International enterprise clients · Engineering-first culture
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative">
            <div className="clip-frame overflow-hidden rounded-2xl border border-tark-line">
              <img src={IMAGES.global} alt="Global technology delivery" className="aspect-[4/3] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-tark-bg/85 via-transparent to-transparent" />
              <div className="absolute right-5 top-5 rounded-lg bg-tark-bg/70 px-3 py-1.5 text-right font-mono text-[10px] uppercase tracking-[0.3em] text-tark-cyan backdrop-blur-sm">
                Finance · Healthcare · Manufacturing · Retail · Logistics
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden rounded-2xl border border-tark-line bg-tark-card p-5 shadow-2xl shadow-black/50 lg:block">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-tark-dim">Built for</p>
              <p className="mt-2 font-display text-sm font-bold text-tark-ink">International enterprise standards</p>
              <p className="mt-1 font-mono text-[10px] text-tark-cyan">Cloud · Data &amp; AI · Software</p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const CaseStudies = () => {
  const [cases, setCases] = useState([]);

  useEffect(() => {
    api.get("/case-studies").then((r) => setCases(r.data)).catch(() => {});
  }, []);

  if (cases.length === 0) return null;

  return (
    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10" data-testid="case-studies">
      <Reveal>
        <SectionTag>Selected Work</SectionTag>
        <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Case Studies</h2>
      </Reveal>
      <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {cases.map((cs, i) => (
          <Reveal key={cs.id} delay={(i % 3) * 0.07}>
            <div className="card-glow h-full rounded-2xl border border-tark-line bg-tark-card p-8" data-testid={`case-study-${cs.id}`}>
              {cs.industry && (
                <span className="rounded border border-tark-blue/40 bg-tark-blue/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-tark-cyan">
                  {cs.industry}
                </span>
              )}
              <h3 className="mt-5 font-display text-lg font-bold leading-snug tracking-tight">{cs.title}</h3>
              {cs.summary && <p className="mt-3 text-sm leading-relaxed text-tark-mut">{cs.summary}</p>}
              {cs.outcomes?.length > 0 && (
                <ul className="mt-5 space-y-2">
                  {cs.outcomes.map((o) => (
                    <li key={o} className="flex items-start gap-2.5 text-sm text-tark-mut">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-tark-cyan" />{o}
                    </li>
                  ))}
                </ul>
              )}
              {cs.tags?.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">
                  {cs.tags.map((t) => (
                    <span key={t} className="rounded border border-tark-line px-2 py-0.5 font-mono text-[10px] text-tark-dim">{t}</span>
                  ))}
                </div>
              )}
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const EngagementModels = () => (
  <section className="border-y border-tark-line bg-tark-surface/40 py-24 lg:py-32" data-testid="engagement-models">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <SectionTag>Engagement Models</SectionTag>
        <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
          Work With TARK Your Way
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {ENGAGEMENT_MODELS.map((m, i) => (
          <Reveal key={m.name} delay={i * 0.08}>
            <div className="card-glow flex h-full flex-col rounded-2xl border border-tark-line bg-tark-card p-7" data-testid={`engagement-${i}`}>
              <span className="font-mono text-xs text-tark-cyan">0{i + 1}</span>
              <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{m.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-tark-mut">{m.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CareersBanner = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10" data-testid="careers-banner">
    <Reveal>
      <div className="relative overflow-hidden rounded-3xl border border-tark-line">
        <img src={IMAGES.careers} alt="Engineering team at TARK" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-tark-bg via-tark-bg/85 to-tark-bg/40" />
        <div className="relative flex flex-col items-start gap-8 p-10 sm:p-16 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-tark-cyan">For Job Seekers</p>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              BUILD YOUR CAREER AT TARK
            </h2>
            <p className="mt-4 max-w-xl text-base text-tark-mut">
              Work with teams building software, cloud, DevOps, data and AI solutions.
            </p>
          </div>
          <PrimaryButton to="/careers" testId="careers-banner-cta">Explore Careers</PrimaryButton>
        </div>
      </div>
    </Reveal>
  </section>
);

const ClosingCTA = () => (
  <section className="relative overflow-hidden border-t border-tark-line py-28 lg:py-36" data-testid="closing-cta">
    <div className="absolute inset-0 hero-grid opacity-40 [mask-image:radial-gradient(ellipse_60%_60%_at_50%_100%,black,transparent)]" />
    <div className="absolute inset-x-0 bottom-0 h-64 bg-[radial-gradient(ellipse_50%_60%_at_50%_100%,rgba(0,102,255,0.22),transparent)]" />
    <div className="relative mx-auto max-w-4xl px-6 text-center">
      <Reveal>
        <SectionTag>Start the Conversation</SectionTag>
        <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
          Ready to Build What&apos;s Next?
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-tark-mut sm:text-lg">
          Whether you are modernizing infrastructure, building enterprise software, adopting AI
          or transforming software delivery, TARK brings the engineering capabilities to move
          your technology forward.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <PrimaryButton to="/start-a-project" testId="closing-talk-to-tark">Talk to TARK</PrimaryButton>
          <GhostButton to="/services" testId="closing-explore-services">Explore Services</GhostButton>
        </div>
      </Reveal>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <Marquee items={MARQUEE_ITEMS} />
    <Capabilities />
    <IndustrySpotlight />
    <SolutionsTeaser />
    <DataAISpotlight />
    <EngagementModels />
    <CaseStudies />
    <CareersBanner />
    <ClosingCTA />
  </>
);

export default Home;
